import { ATSOrigin, BrowserBloomer, GqlAccountManager } from '@bloomays-lib/types.shared';
import RawCard from '@mui/material/Card';
import RawCardHeader from '@mui/material/CardHeader';
import RawPermIdentityIcon from '@mui/icons-material/PermIdentity';
import RawMailOutlineIcon from '@mui/icons-material/MailOutline';
import RawFileOpenIcon from '@mui/icons-material/FileOpen';
import RawPhoneIcon from '@mui/icons-material/Phone';
import RawVisibilityIcon from '@mui/icons-material/Visibility';
import RawPeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import { useEffect, useState } from 'react';
import { Button, FileViewer, HrefLink, Modal } from '@bloomays-lib/ui.shared';
import RawIconButton from '@mui/material/IconButton';
import RawLinkIcon from '@mui/icons-material/Link';
import CardContent from '@mui/material/CardContent';
import { UpdateBloomerForm } from './UpdateBloomerForm';
import { Theme, styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useAuthStore } from '../../stores';
import { useTranslation } from 'react-i18next';

type BloomerCardProps = {
  missionOrigin?: ATSOrigin;
  missionJobOwner: GqlAccountManager | null;
  bloomer: BrowserBloomer;
  filePath: string | undefined;
  setFilePath: React.Dispatch<React.SetStateAction<string | undefined>>;
};

const LinkToCard = (args: Pick<BloomerCardProps, 'missionOrigin' | 'bloomer'>) => {
  const { missionOrigin, bloomer } = args;

  if (!missionOrigin) return;

  if (missionOrigin === 'pipefy' && bloomer.pipefyContactId) {
    return (
      <HrefLink
        icon={<LinkIcon />}
        href={`https://app.pipefy.com/apollo_databases/302454076/records/${bloomer.pipefyContactId}`}
        target="_blank"
      />
    );
  }

  if (missionOrigin === 'hiresweet' && bloomer.hiresweetContactId) {
    return (
      <HrefLink
        icon={<LinkIcon />}
        href={`${import.meta.env.VITE_HIRESWEET_FRONT_URL}/reveal/search?profileId=${bloomer.hiresweetContactId}`}
        target="_blank"
      />
    );
  }

  return;
};

export const BloomerCard = ({ bloomer, filePath, setFilePath, missionOrigin, missionJobOwner }: BloomerCardProps) => {
  const { t } = useTranslation(['mission']);
  const [openPopin, setOpenPopin] = useState<boolean>(false);
  const [token, setToken] = useState<string | undefined>();
  const auth = useAuthStore((state) => state.auth);

  const missionJobOwnerDisplayName = missionJobOwner
    ? [missionJobOwner.firstname, missionJobOwner.lastname].filter(Boolean).join(' ').trim() || missionJobOwner.email
    : null;

  useEffect(() => {
    if (auth?.getAccessTokenSilently) {
      auth.getAccessTokenSilently().then((token) => {
        setToken(token);
      });
    }
  }, [auth]);

  return (
    <Card>
      <Modal
        open={!!filePath}
        onClose={() => setFilePath(undefined)}
        additionnalCSS={{
          width: '822px',
          padding: '32px',
          border: 'none',
          borderRadius: '20px',
        }}
      >
        <FileViewer width="100%" height="1000px" url={filePath as string} accessToken={token} />
      </Modal>
      <Modal
        open={openPopin}
        onClose={() => {
          setOpenPopin(false);
        }}
        additionnalCSS={{
          padding: '20px 30px',
          width: '100%',
          maxWidth: '512px',
        }}
      >
        <UpdateBloomerForm
          callback={() => {
            setOpenPopin(false);
          }}
          bloomer={bloomer}
        />
      </Modal>
      <CardContent>
        <ContainerLine>
          <CardHeader title={'Le bloomer'} titleTypographyProps={{ variant: 'heading6' }} />
          <LinkToCard missionOrigin={missionOrigin} bloomer={bloomer} />
        </ContainerLine>

        <ContainerData>
          <ContainerLine>
            <CenteredLabel id="fullnameBloomerCard" variant="body1Light" component="p">
              <PermIdentityIcon />
              Nom complet:
            </CenteredLabel>
            <DarkTypo id="fullnameBloomerCardValue" variant="body1Light">
              {bloomer.fullname || (bloomer.firstname && `${bloomer.firstname} ${bloomer.lastname}`)}
            </DarkTypo>
          </ContainerLine>
          <ContainerLine>
            <CenteredLabel id="emailBloomerCard" variant="body1Light" component="p">
              <MailOutlineIcon />
              Email:
            </CenteredLabel>
            <DarkTypo id="emailBloomerCardValue" variant="body1Light">
              <HrefLine href={`mailto:${bloomer?.email}`}>{bloomer?.email}</HrefLine>
            </DarkTypo>
          </ContainerLine>
          <ContainerLine>
            <CenteredLabel id="emailSecBloomerCard" variant="body1Light" component="p">
              <MailOutlineIcon />
              Email secondaire:
            </CenteredLabel>
            <DarkTypo id="emailSecBloomerCardValue" variant="body1Light">
              <HrefLine href={`mailto:${bloomer?.secondaryEmail}`}>{bloomer?.secondaryEmail}</HrefLine>
            </DarkTypo>
          </ContainerLine>
          <ContainerLine>
            <CenteredLabel id="phoneNumberBloomerCard" variant="body1Light" component="p">
              <PhoneIcon />
              Téléphone:
            </CenteredLabel>
            <DarkTypo id="phoneNumberBloomerCardValue" variant="body1Light">
              <HrefLine href={`tel:${bloomer?.phoneNumber}`}>{bloomer?.phoneNumber}</HrefLine>
            </DarkTypo>
          </ContainerLine>
          <ContainerLine>
            <CenteredLabel id="JobOwnerBloomerCard" variant="body1Light" component="p">
              <PeopleOutlineIcon />
              {t('jobOwnerLabel')} :
            </CenteredLabel>
            <DarkTypo id="JobOwnerBloomerCardValue" variant="body1Light">
              {missionJobOwnerDisplayName || t('notAvailable')}
            </DarkTypo>
          </ContainerLine>
          {bloomer?.credentialID && (
            <ContainerLine>
              <CenteredLabel variant="body1Light" component="p">
                <FileOpenIcon />
                CI :
              </CenteredLabel>
              <IconButton
                onClick={() => setFilePath(bloomer?.credentialID?.toString())}
                title={bloomer?.credentialID?.toString()}
              >
                <VisibilityIcon />
              </IconButton>
            </ContainerLine>
          )}
        </ContainerData>
      </CardContent>
      <Button
        sx={{ position: 'absolute', right: 10, bottom: 10 }}
        textButton="Modifier"
        onClick={() => {
          setOpenPopin(true);
        }}
      />
    </Card>
  );
};

const iconCSS = (args: { theme: Theme }) => ({
  paddingRight: 10,
  color: args.theme.palette.neutral[50],
  width: 20,
});

const iconVCSS = (args: { theme: Theme }) => ({
  paddingTop: 0,
  color: args.theme.palette.neutral[50],
  width: 20,
});

const Card = styled(RawCard)(({ theme }) => ({
  width: '100%',
  position: 'relative',
  maxWidth: 400,
  margin: 20,
  [theme.breakpoints.up('lg')]: {
    maxWidth: 600,
  },
}));

const CardHeader = styled(RawCardHeader)(({ theme }) => ({
  textTransform: 'uppercase',
  textAlign: 'left',
}));

const ContainerLine = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
}));

const ContainerData = styled('div')(() => ({
  paddingLeft: 5,
}));

const CenteredLabel = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  textAlign: 'left',
  minWidth: 'auto',
  whiteSpace: 'nowrap',
  [theme.breakpoints.down('sm')]: {
    whiteSpace: 'normal',
  },
})) as typeof Typography;

const DarkTypo = styled(Typography)(({ theme }) => ({
  paddingLeft: 5,
  color: theme.palette.blue[60],
}));

const HrefLine = styled('a')(() => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
}));

const PermIdentityIcon = styled(RawPermIdentityIcon)(iconCSS);
const MailOutlineIcon = styled(RawMailOutlineIcon)(iconCSS);
const PhoneIcon = styled(RawPhoneIcon)(iconCSS);
const PeopleOutlineIcon = styled(RawPeopleOutlineIcon)(iconCSS);
const FileOpenIcon = styled(RawFileOpenIcon)(iconCSS);
const VisibilityIcon = styled(RawVisibilityIcon)(iconVCSS);
const IconButton = styled(RawIconButton)(({ theme }) => ({
  paddingTop: '0px',
  paddingBottom: '0px',
}));
const LinkIcon = styled(RawLinkIcon)(({ theme }) => ({
  color: theme.palette.neutral[50],
}));
